/**
 * The settings routes behind where a cloud run's skills come from (YAG-784).
 * The sections take these as injectable props so tests never reach the
 * network; a refusal carries the server's own customer-safe sentence.
 */
import type {
  PluginMarketplacePreviewRequest,
  PluginMarketplacePreviewResponse,
  PluginMarketplaceResponse,
  PluginMarketplaceUpdateRequest,
  SkillsRepoResponse,
  SkillsRepoUpdateRequest,
  SkillsRepoUpdateResponse,
} from "@yagni/shared";

/** The server's `{ error }` when it sent one, else the fallback. */
export async function readError(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return typeof body?.error === "string" && body.error.trim() ? body.error : fallback;
}

async function send<T>(url: string, method: string, fallback: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "include",
    ...(body === undefined
      ? {}
      : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  });
  if (!res.ok) throw new Error(await readError(res, fallback));
  return (await res.json().catch(() => ({}))) as T;
}

const MARKETPLACE = "/api/agent-sources/marketplace";
const SKILLS_REPO = "/api/account/skills-repo";

export type FetchMarketplace = () => Promise<PluginMarketplaceResponse>;
export type PreviewMarketplace = (input: PluginMarketplacePreviewRequest) => Promise<PluginMarketplacePreviewResponse>;
export type SaveMarketplace = (input: PluginMarketplaceUpdateRequest) => Promise<void>;
export type RemoveMarketplace = () => Promise<void>;

export const fetchMarketplace: FetchMarketplace = () =>
  send<PluginMarketplaceResponse>(MARKETPLACE, "GET", "The plugin marketplace could not be read.");
export const previewMarketplace: PreviewMarketplace = (input) =>
  send<PluginMarketplacePreviewResponse>(`${MARKETPLACE}/preview`, "POST", "The marketplace could not be read just now.", input);
export const saveMarketplace: SaveMarketplace = async (input) => {
  await send(MARKETPLACE, "PUT", "The plugin marketplace was not saved.", input);
};
export const removeMarketplace: RemoveMarketplace = async () => {
  await send(MARKETPLACE, "DELETE", "The plugin marketplace was not removed.");
};

export type FetchSkillsRepo = () => Promise<SkillsRepoResponse>;
export type SaveSkillsRepo = (input: SkillsRepoUpdateRequest) => Promise<SkillsRepoUpdateResponse>;
export type RemoveSkillsRepo = () => Promise<void>;

export const fetchSkillsRepo: FetchSkillsRepo = () =>
  send<SkillsRepoResponse>(SKILLS_REPO, "GET", "Your skills repository could not be read.");
export const saveSkillsRepo: SaveSkillsRepo = (input) =>
  send<SkillsRepoUpdateResponse>(SKILLS_REPO, "PUT", "Your skills repository was not saved.", input);
export const removeSkillsRepo: RemoveSkillsRepo = async () => {
  await send(SKILLS_REPO, "DELETE", "Your skills repository was not removed.");
};
